// covered-by: frontends/carrie/src/ask-v2/services/__tests__/reviews.test.ts
import {
  createAskPunttReviewCommentResponseSchema,
  getAskPunttReviewResponseSchema,
  updateAskPunttReviewCommentResponseSchema,
  type AskPunttReviewCommentWriteBody,
  type CreateAskPunttReviewCommentResponse,
  type GetAskPunttReviewResponse,
  type UpdateAskPunttReviewCommentResponse,
} from "@mg/schemas/src/christo/askPunttReview";

import { API_URL } from "../../config/env";
import { withReadableCitations } from "../../utils/citationRepair";
import { smartFetch } from "../../utils/http";

const reviewsBase = `${API_URL}/private/catalyst/ask-puntt/reviews`;

/**
 * The review payload with every unreadable comment citation repaired or dropped
 * (`utils/citationRepair`). A review arrives WRAPPED, so only the unwrapping
 * lives here: `askPunttReviewCommentSchema.citations` is an array of a UNION,
 * and one unreadable entry would otherwise fail its comment, fail the review,
 * and throw — rendering a review of seven findings as an error because one chip
 * was malformed.
 */
function withReadableReviewCitations(json: unknown): unknown {
  const root = json as { review?: { comments?: unknown } } | null;
  const comments = root?.review?.comments;
  if (!Array.isArray(comments)) return json;
  const readable = withReadableCitations(comments);
  if (readable === comments) return json;
  return {
    ...(root as object),
    review: {
      ...(root as { review: object }).review,
      comments: readable,
    },
  };
}

export async function getAskPunttReview(
  reviewId: string,
): Promise<GetAskPunttReviewResponse> {
  const json = await smartFetch(`${reviewsBase}/${reviewId}`);
  return getAskPunttReviewResponseSchema.parse(
    withReadableReviewCitations(json),
  );
}

/**
 * Adds a human comment to a V2 review. christo stamps `createdBy` from the
 * caller's token and stores the pin location (`rel_x`/`rel_y`/`page_num`) on
 * the review document.
 */
export async function createAskPunttReviewComment(
  reviewId: string,
  body: AskPunttReviewCommentWriteBody,
): Promise<CreateAskPunttReviewCommentResponse> {
  const json = await smartFetch(`${reviewsBase}/${reviewId}/comments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return createAskPunttReviewCommentResponseSchema.parse(json);
}

export async function updateAskPunttReviewComment(
  reviewId: string,
  commentId: string,
  body: Partial<AskPunttReviewCommentWriteBody>,
): Promise<UpdateAskPunttReviewCommentResponse> {
  const json = await smartFetch(
    `${reviewsBase}/${reviewId}/comments/${commentId}`,
    {
      method: "PUT",
      body: JSON.stringify(body),
    },
  );
  return updateAskPunttReviewCommentResponseSchema.parse(json);
}
